<template>
    <div>
        <chart v-model="chart"></chart>

        <!--chart footer-->
        <div class="flex items-center justify-between h-64 mt-16 border-t border-blue-light">
            <div @click="setPanelType('Insights')" class="flex items-center cursor-pointer">
                <div v-if="hasGivers" :class="numberOfGivers > 1 ? 'mr-30' : 'mr-10'" class="flex">
                    <span v-if="numberOfGivers > maxAvatars" class="-mr-20 avatar avatar-more">...</span>

                    <template v-for="giver in displayGivers">
                        <img :src="giver.avatar || `/images/placeholders/avatar.png`" :alt="giver.full_name" :class="numberOfGivers > 1 ? '-mr-20' : ''" class="avatar"  :key="giver.id">
                    </template>
                </div>

                <span class="text-14">
                    <span class="hover:underline" v-html="insightText"></span>
                    <span v-if="hasInsights" class="text-blue-light">({{ totalInsights }})</span>
                </span>
            </div>
            <div class="flex">
                <div @click="setPanelType('Actions')" class="flex items-center cursor-pointer">
                    <template v-if="hasUncompletedActions">
                        <img src="images/bell.svg" alt="Bell">
                        <span v-if="numberOfActions > 1" class="ml-4 font-14">{{ totalActiveActions }}</span>
                    </template>
                    <img v-else src="images/bell-o.svg" alt="Bell">
                </div>

                <favourites v-model="chart" :favourite="favourite"></favourites>
            </div>
        </div>

        <insights
            v-if="showInsights"
            v-model="insights"
            :total="totalInsights"
            @create="onCreateInsight"
            @delete="onDeleteInsight"
            @loadmore="loadMoreInsights"
        ></insights>
        <actions
            v-if="showActions"
            v-model="actions"
            :total="totalActions"
            @active="onActiveAction"
            @complete="onCompleteAction"
            @create="onCreateAction"
            @loadmore="loadMoreActions"
        ></actions>
    </div>
</template>

<script>
    import Actions from "./actions/actions"
    import { call, get } from 'vuex-pathify'
    import { camelCase, cloneDeep, ceil, isFinite, sortBy, uniqBy } from "lodash"
    import Favourites from "./favourites"
    import Insights from "./insights/insights"

    export default {
        props: {
            value: {
                type: Object,
                required: true,
            },
        },

        components: {
            Actions,
            Favourites,
            Insights,
        },

        data() {
            return {
                batchMore: 3,

                chart: this.value,

                chartData: {
                    labels: [],
                    datasets: [],
                    isInverted: false,
                },

                dataError: null,

                loading: true,

                panelType: '',

                maxAvatars: 3,
            }
        },

        computed: {
            user: get('auth/user'),

            actions() {
                return this.$store.get('charts/actions', this.chart.id)
            },

            displayGivers() {
                if (!this.hasGivers) {
                    return [];
                }

                if (this.numberOfGivers <= this.maxAvatars) {
                    return this.givers
                }

                return this.givers.slice(0, this.maxAvatars)
            },

            chartType() {
                return `${camelCase(this.chart.type.slug)}Chart`
            },

            favourite() {
                return this.chart.favourites.find(favourite => favourite.user.id === this.user.id) ? true : false
            },

            givers() {
                return this.$store.get('charts/insightGivers', this.chart.id)
            },

            hasChartData() {
                return this.chartData.labels.length > 0 && this.chartData.datasets.length
            },

            hasDataError() {
                return this.dataError !== null
            },

            hasGivers() {
                return this.givers && this.givers.length > 0 ? true : false
            },

            hasInsights() {
                return this.insights && this.insights.length > 0 ? true : false
            },

            hasUncompletedActions() {
                return this.actions && this.actions.filter(action => action.state !== 'completed').length > 0 ? true : false
            },

            insights() {
                return this.$store.get('charts/insights', this.chart.id)
            },

            insightText() {
                if (this.totalInsights === 0) {
                    return 'Add your comment&hellip;'
                }

                if (this.totalInsights === 1) {
                    return 'Comment'
                }

                if (this.totalInsights >= 1) {
                    return 'Comments'
                }
            },

            numberOfActions() {
                return this.actions.length
            },

            numberOfGivers() {
                return this.givers.length
            },

            totalActions() {
                return this.$store.get('charts/totalActions', this.chart.id)
            },

            totalActiveActions() {
                return this.$store.get('charts/totalActiveActions', this.chart.id)
            },

            totalInsights() {
                return this.$store.get('charts/totalInsights', this.chart.id)
            },

            showActions() {
                return this.panelType === 'Actions'
            },

            showInsights() {
                return this.panelType === 'Insights'
            },
        },

        async mounted () {
            await this.loadChartInsights(this.chart)
            // await this.loadChartInsightGivers(this.chart)
            await this.loadChartActions(this.chart)
        },

        methods: {
            activedAction: call('actions/actived'),
            completedAction: call('actions/completed'),
            createAction: call('charts/createAction'),
            createInsight: call('charts/createInsight'),
            destroyInsight: call('insights/destroy'),
            loadChartActions: call('charts/loadChartActions'),
            // loadChartInsightGivers: call('charts/loadChartInsightGivers'),
            loadChartInsights: call('charts/loadChartInsights'),

            async loadMoreActions() {
                this.chart.query_settings.action.limit += this.batchMore

                await this.loadChartActions(this.chart)
            },

            async loadMoreInsights() {
                this.chart.query_settings.insight.limit += this.batchMore

                await this.loadChartInsights(this.chart)
            },

            async onActiveAction(action) {
                await this.activedAction(action)
                await this.loadChartActions(this.chart)
                this.chart.active_actions_count += 1
            },

            async onCompleteAction(action) {
                await this.completedAction(action)
                await this.loadChartActions(this.chart)
                this.chart.active_actions_count -= 1
            },

            async onCreateAction(action) {
                this.createAction({chart: this.chart, action: action})
            },

            async onCreateInsight(insight) {
                this.createInsight({chart: this.chart, insight: insight})
            },

            async onDeleteInsight(insight) {
                await this.destroyInsight(insight)
                await this.loadChartInsights(this.chart)
            },

            setPanelType(type) {
                if (this.panelType === type) {
                    this.panelType = ''
                } else [
                    this.panelType = type
                ]
            },
        }
    }
</script>
